import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import type { FormField, FormState, FormContextType, FormProviderProps } from "../../interfaces/components/form";

const FormContext = createContext<FormContextType | null>(null);

export const useForm = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error("useForm must be used within a FormProvider");
    }
    return context;
};

export const FormProvider: React.FC<FormProviderProps> = ({
                                                              children,
                                                          }) => {
    const [fields, setFields] = useState<FormState>({});
    const [showErrors, setShowErrors] = useState(false);

    const initializeFields = useCallback((initialFields: FormState) => {
        setFields(initialFields);
    }, []);

    const validateField = useCallback(
        (name: string, value: any): string[] => {
            const field = fields[name];
            if (!field) {
                console.warn(`Field ${name} not found in form state`);
                return [];
            }

            const errors: string[] = [];

            // Required validation - only if the field is required and has no value
            if (
                field.required &&
                (!value || (typeof value === "string" && value.trim() === ""))
            ) {
                errors.push(`${field.label || name} is required`);
            }

            // Format validation - only if there's a value to validate
            if (value && field.format === "email") {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    errors.push("Please enter a valid email address");
                }
            }

            // Custom validators - only if there's a value to validate
            if (value && field.validator) {
                field.validator.forEach((validator) => {
                    if (validator.regex) {
                        const regex = new RegExp(validator.regex);
                        if (!regex.test(value)) {
                            errors.push(validator.message);
                        }
                    }
                });
            }

            return errors;
        },
        [fields],
    );

    const setFieldValue = useCallback(
        (name: string, value: any) => {
            setFields((prev) => {
                const field = prev[name];
                if (!field) {
                    console.warn(`Field ${name} not found in form state`);
                    return prev;
                }

                // Validate the field with the new value
                const errors = validateField(name, value);

                return {
                    ...prev,
                    [name]: {
                        ...field,
                        value,
                        errors,
                    },
                };
            });
        },
        [validateField],
    );

    const setFieldTouched = useCallback((name: string, touched: boolean) => {
        setFields((prev) => {
            const field = prev[name];
            if (!field) {
                console.warn(`Field ${name} not found in form state`);
                return prev;
            }
            return {
                ...prev,
                [name]: {
                    ...field,
                    touched,
                },
            };
        });
    }, []);

    const validateForm = useCallback((): boolean => {
        let isValid = true;

        Object.entries(fields).forEach(([name, field]) => {
            const errors = validateField(name, field.value);
            if (errors.length > 0) {
                isValid = false;
                setFields((prev) => ({
                    ...prev,
                    [name]: {
                        ...prev[name],
                        errors,
                    },
                }));
            }
        });

        return isValid;
    }, [fields, validateField]);

    const resetForm = useCallback(() => {
        setFields({});
        setShowErrors(false);
    }, []);

    // Handle autofill
    useEffect(() => {
        const handleAutofill = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target && target.name && fields[target.name]) {
                setFieldValue(target.name, target.value);
            }
        };

        document.addEventListener("animationstart", handleAutofill);
        return () => {
            document.removeEventListener("animationstart", handleAutofill);
        };
    }, [fields, setFieldValue]);

    const value = {
        fields,
        setFieldValue,
        setFieldTouched,
        validateField,
        validateForm,
        resetForm,
        showErrors,
        setShowErrors,
        initializeFields,
    };

    return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
